import React, { useState } from 'react';
import { api, MagentoOperationsSummary, OperationResult } from '../api';
import { Callout, IconButton, RowActions, Section, Subsection, Table } from './primitives';
import { StatusBadge } from './ui';

/**
 * The indexer's own status word, not "Ready or Stale": "working" is neither,
 * and an unread status must not be reported as a stale index.
 */
interface MagentoOperationsCardProps {
  environmentId: string;
}

export const MagentoOperationsCard: React.FC<MagentoOperationsCardProps> = ({ environmentId }) => {
  const [summary, setSummary] = useState<MagentoOperationsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<OperationResult | null>(null);

  const fetchOperations = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getMagentoOperations(environmentId);
      setSummary(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Commerce Ops could not read the indexers and consumers.');
    } finally {
      setLoading(false);
    }
  };

  const runReindex = async (indexerId = 'all') => {
    setActionLoading('reindex:' + indexerId);
    setError(null);
    setLastResult(null);
    try {
      const res = await api.triggerReindex(environmentId, indexerId);
      setLastResult(res);
      await fetchOperations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The reindex failed.');
    } finally {
      setActionLoading(null);
    }
  };

  const runCron = async () => {
    setActionLoading('cron');
    setError(null);
    setLastResult(null);
    try {
      const res = await api.triggerCronRun(environmentId);
      setLastResult(res);
      await fetchOperations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The cron run failed.');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Section
      title="Magento operations"
      testId="magento-operations"
      actions={
        <>
          <button type="button" onClick={fetchOperations} disabled={loading} className="button button--secondary">
            {loading ? 'Refreshing' : 'Refresh status'}
          </button>
          <button type="button" onClick={runCron} disabled={actionLoading !== null} className="button button--secondary">
            {actionLoading === 'cron' ? 'Running cron' : 'Run cron now'}
          </button>
          <button type="button" onClick={() => runReindex('all')} disabled={actionLoading !== null} className="button button--secondary">
            {actionLoading === 'reindex:all' ? 'Reindexing' : 'Reindex all'}
          </button>
        </>
      }
    >
      {error ? (
        <Callout tone="danger" title="That did not work">
          {error}
        </Callout>
      ) : null}

      {lastResult ? (
        <Callout
          tone="success"
          title={
            <>
              Action: <code>{lastResult.action}</code> finished in <span className="tabular-nums">{lastResult.duration_ms} ms</span>
            </>
          }
        >
          <pre className="ssh-output">{lastResult.output || 'Finished without output.'}</pre>
        </Callout>
      ) : null}

      {summary ? (
        <>
          <Subsection
            title={summary.indexers_unavailable_reason ? 'Indexers' : `Indexers (${summary.indexers.length})`}
            description={summary.stale_indexer_count ? `${summary.stale_indexer_count} need a reindex` : undefined}
          >
            {summary.indexers_unavailable_reason ? (
              <p className="quiet-empty" data-testid="indexers-unknown">
                Not verified · {summary.indexers_unavailable_reason}
              </p>
            ) : (
              <Table label="Indexers" compact className="magento-ops__indexers">
                <thead>
                  <tr>
                    <th scope="col">Indexer</th>
                    <th scope="col">Last run</th>
                    <th scope="col">Status</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summary.indexers.map((idx) => (
                    <tr key={idx.id}>
                      <th scope="row">{idx.title}</th>
                      <td className="tabular-nums muted">{idx.last_runtime || 'Never run'}</td>
                      <td>
                        <StatusBadge kind="magentoIndexer" value={idx.status} />
                      </td>
                      <td>
                        <RowActions>
                          <IconButton
                            icon="refresh"
                            size="sm"
                            variant="ghost"
                            label={`Reindex ${idx.title}`}
                            onClick={() => runReindex(idx.id)}
                            disabled={actionLoading !== null}
                          />
                        </RowActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Subsection>

          <Subsection title={summary.consumers_unavailable_reason ? 'Message queue consumers' : `Running message queue consumers (${summary.consumers.length})`}>
            {summary.consumers_unavailable_reason ? (
              <p className="quiet-empty" data-testid="consumers-unknown">
                Not verified · {summary.consumers_unavailable_reason}
              </p>
            ) : (
              <ul className="status-list" aria-label="Message queue consumers">
                {summary.consumers.map((c, i) => (
                  <li key={`${c.name}-${i}`} className="status-list__item">
                    <span className="status-list__name font-mono">{c.name}</span>
                    <StatusBadge kind="process" value={c.status} label={c.status === 'running' ? `Running (PID ${c.pid})` : undefined} />
                  </li>
                ))}
              </ul>
            )}
          </Subsection>
        </>
      ) : (
        <p className="quiet-empty">Not read yet</p>
      )}
    </Section>
  );
};
